import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ChevronRight, ChevronDown, BookOpen, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import BetaFeedbackPanel from './BetaFeedbackPanel';
import { useLessonContent } from './useLessonContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default content
const DEFAULT_SECTIONS = {
  opening_verse: '"These were more noble than those in Thessalonica, in that they received the word with all readiness of mind, and searched the scriptures daily, whether those things were so."',
  opening_verse_ref: 'Acts 17:11',
  secondary_verse: '"For precept must be upon precept, precept upon precept, line upon line, line upon line: here a little, and there a little."',
  secondary_verse_ref: 'Isaiah 28:10',
  opening_p1: 'Those people were called noble — not because they were smarter or more educated, but because they were willing to check. They heard a teaching and then opened their Bibles to see if what they were told was actually there. That is the only qualification required for what you are about to do.',
  opening_p2: "Solomon explained the importance of using wisdom and knowledge of the basic principle of grammar to understand the word of God and expose man's narratives and errors. Isaiah explains how it's done. Line upon line, precept upon precept. Word upon word. And a little here and a little there.",
  what_is_git_p1: 'The Grammar Integrity Test is a three-question tool you can apply to any verse in your Bible. You do not need Greek. You do not need a seminary degree. You do not need any special training. You only need to be willing to ask three plain questions and let the words on the page answer them.',
  what_is_git_p2: "Three questions. That's it. The same three questions every time. The same three questions for every verse.",
  illustration_intro: "Before we test this on a Bible verse, let's make sure the tool is clear. Read this sentence:",
  illustration_sentence: '"The dog cannot go outside until it stops raining."',
  illustration_conclusion: 'That is the whole test. Three questions. You already knew the answer before you applied the test — because you read the sentence for what it said. That is what the Grammar Integrity Test does. It keeps you doing what you already naturally do when you read any sentence — until you get to a Bible verse. Then for some reason we stop reading it like a sentence and start reading it like a theology puzzle. The GIT fixes that. It simply asks you to read the Bible the same way you read everything else.',
  encouragement_text: "You have been doing this your whole life. You read a cereal box and ask: what does this say? You read a text message and ask: what does my friend mean? The only place most people stop doing this is when they open their Bible — because they have been told they need an expert to translate it for them. You do not. The words on the page mean what they say. The GIT just keeps you honest. And focused on the grammar — the syntax of meaning God already defined.",
  berean_text: "The Bereans in Acts 17 were called noble for one reason: they were willing to check. They heard Paul — the Apostle Paul, the greatest Christian theologian who ever lived — and they went home and opened their Bibles to see if what he said was actually there. That is the only standard this entire study holds itself to. Not a seminary. Not a tradition. Not a doctrine. Not a theory. Not a presumption. Not a narrative of man. Not a denomination. The words on the page. The narrative God gave. The Plan He gave in His Scripture. If you are willing to check — you are ready.",
  bridge_text: "You have the tool. Now let's pick up the second key — the Blueprints.",
  bridge_next: 'Next: Legend Key 2 — God\'s Divine Rehearsals',
};

const DEFAULT_ARRAYS = {
  git_questions: [
    { num: '1', question: 'WHO?', label: 'The Subject', desc: 'Who is being talked about? Who is acting, or being acted upon?' },
    { num: '2', question: 'WHAT?', label: 'The Verb / Action', desc: 'What is happening? What action is described, promised, or commanded?' },
    { num: '3', question: 'WHEN?', label: 'The Timeline / Condition', desc: 'When does this happen? What condition must be met first? Is there a sequence word — until, after, before, except, when?' },
  ],
  dog_answers: [
    { q: 'WHO?', a: "The dog. That's the subject." },
    { q: 'WHAT?', a: "Cannot go outside. That's the verb — the action blocked." },
    { q: 'WHEN?', a: 'Until it stops raining. That\'s the condition. UNTIL is the timing word. The dog is blocked until a specific thing happens first.' },
  ],
  git_table: [
    { is: 'A method for reading what the text says — consistently, every time, without adjusting the answer to fit a conclusion already reached.', isnt: 'A new theological system.' },
    { is: 'A reading tool — the same three questions applied to every text, in every lesson, without exception.', isnt: 'A replacement for study.' },
    { is: '', isnt: 'An argument.' },
  ],
};

export default function GIT101Lesson() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const theme = useTheme();
  const [revealedQuestions, setRevealedQuestions] = useState({});
  const [revealedDog, setRevealedDog] = useState({});
  const [completing, setCompleting] = useState(false);
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';
  const { c, arr } = useLessonContent('GIT_101', DEFAULT_SECTIONS, DEFAULT_ARRAYS);

  const toggleReveal = (key, setter) => setter(prev => ({ ...prev, [key]: !prev[key] }));

  const handleCompleteModule = useCallback(async () => {
    if (!token && !user) {
      toast.info('Sign in to save your progress and continue');
      sessionStorage.setItem('pendingLessonCompletion', JSON.stringify({ moduleKey: 'GIT_101', nextRoute: '/lesson/blueprints' }));
      navigate('/login', { state: { from: { pathname: '/lesson/git-101' } } });
      return;
    }
    setCompleting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/learning/progress/GIT_101/start`, { method: 'POST', headers, credentials: 'include' });
      const res = await fetch(`${API_URL}/api/learning/progress/GIT_101/complete`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        toast.success('GIT 101 complete! Taking you to the Blueprints...');
        setTimeout(() => navigate('/lesson/blueprints'), 1500);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Could not complete module');
      }
    } catch { toast.error('Error completing module'); }
    setCompleting(false);
  }, [token, user, navigate]);

  // Auto-complete if returning from login with a pending action
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingLessonCompletion');
    if (pending && (token || user)) {
      try {
        const { moduleKey, nextRoute } = JSON.parse(pending);
        if (moduleKey === 'GIT_101') {
          sessionStorage.removeItem('pendingLessonCompletion');
          (async () => {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            await fetch(`${API_URL}/api/learning/progress/GIT_101/start`, { method: 'POST', headers, credentials: 'include' });
            const res = await fetch(`${API_URL}/api/learning/progress/GIT_101/complete`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
              toast.success('GIT 101 complete! Taking you to the Blueprints...');
              setTimeout(() => navigate(nextRoute), 1500);
            }
          })();
        }
      } catch {}
    }
  }, [token, user, navigate]);

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, fontFamily: "'Times New Roman', 'Georgia', serif" }}>
      {/* Top nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm transition-colors" style={{ color: theme.navText }}
          data-testid="back-to-map">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to the Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted, fontFamily: "'Times New Roman', serif" }}>Legend Key 1</span>
        {isAdmin ? (
          <button onClick={() => navigate('/admin/lesson-content/GIT_101')} className="flex items-center gap-1.5 text-xs" style={{ color: theme.accentPrimary }} data-testid="btn-edit-content">
            <PenTool className="w-3.5 h-3.5" /> Edit Content
          </button>
        ) : <div className="w-20" />}
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-12">

        {/* Opening Verse */}
        <div className="mb-12 text-center" data-testid="opening-verse">
          <p className="text-xs tracking-widest uppercase mb-6" style={{ color: theme.labelColor, fontFamily: "'Times New Roman', serif" }}>THE GIT — GRAMMAR INTEGRITY TEST</p>
          <blockquote className="text-base italic leading-relaxed mb-3 pl-5" style={{ color: theme.scriptureText, borderLeft: `3px solid ${theme.accentPrimary}` }}>
            {c('opening_verse')}
          </blockquote>
          <p className="text-sm" style={{ color: theme.textSecondary }}>— {c('opening_verse_ref')}</p>
        </div>

        <div className="mb-10" style={{ borderLeft: `3px solid ${theme.accentPrimary}`, paddingLeft: '1.25rem' }}>
          <blockquote className="text-sm italic leading-relaxed mb-2" style={{ color: theme.scriptureText }}>
            {c('secondary_verse')}
          </blockquote>
          <p className="text-sm" style={{ color: theme.textSecondary }}>— {c('secondary_verse_ref')}</p>
        </div>

        {/* Opening Paragraph */}
        <div className="mb-12" data-testid="opening-paragraph">
          <p className="text-base leading-relaxed mb-6" style={{ color: theme.textPrimary }}>
            {c('opening_p1')}
          </p>
          <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('opening_p2')}
          </p>
        </div>

        {/* Section: What is the GIT? */}
        <h2 className="text-xl font-bold mb-6 tracking-wide" style={{ color: theme.labelColor }} data-testid="section-what-is-git">WHAT IS THE GIT?</h2>
        <p className="text-base leading-relaxed mb-8" style={{ color: theme.textPrimary }}>
          {c('what_is_git_p1')}
        </p>
        <p className="text-base leading-relaxed mb-10" style={{ color: theme.textPrimary }}>
          {c('what_is_git_p2')}
        </p>

        {/* THE THREE QUESTIONS — Interactive Reveal */}
        <div className="mb-12 space-y-4" data-testid="three-questions">
          {arr('git_questions').map((gq, idx) => (
            <button key={idx}
              onClick={() => toggleReveal(idx, setRevealedQuestions)}
              className="w-full text-left rounded-xl p-5 border transition-all"
              style={{
                background: revealedQuestions[idx] ? 'rgba(30,58,95,0.15)' : 'rgba(10,7,3,0.7)',
                borderColor: revealedQuestions[idx] ? 'rgba(30,58,95,0.4)' : 'rgba(120,80,20,0.15)',
              }}
              data-testid={`git-question-${idx}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold" style={{ color: theme.accentPrimary }}>Question {gq.num}</span>
                  <span className="text-lg font-bold" style={{ color: theme.accentPrimary }}> — {gq.question}</span>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${revealedQuestions[idx] ? 'rotate-180' : ''}`} style={{ color: theme.navText }} />
              </div>
              {revealedQuestions[idx] && (
                <div className="mt-4 pl-2">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{ color: theme.labelColor }}>{gq.label}</p>
                  <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>{gq.desc}</p>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* GRADE SCHOOL ILLUSTRATION */}
        <div className="mb-12 rounded-xl border p-6" style={{ background: theme.bgCard, borderColor: theme.borderSecondary }} data-testid="dog-illustration">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: theme.labelColor }}>GRADE SCHOOL ILLUSTRATION</p>
          <p className="text-base leading-relaxed mb-4" style={{ color: theme.textPrimary }}>
            {c('illustration_intro')}
          </p>
          <p className="text-lg font-bold text-center my-6" style={{ color: theme.accentPrimary }}>{c('illustration_sentence')}</p>
          <p className="text-base leading-relaxed mb-6" style={{ color: theme.textPrimary }}>Now apply the three questions:</p>

          <div className="space-y-3">
            {arr('dog_answers').map((item, i) => (
              <button key={i}
                onClick={() => toggleReveal(i, setRevealedDog)}
                className="w-full text-left rounded-lg p-4 border transition-all"
                style={{
                  background: revealedDog[i] ? 'rgba(212,165,74,0.08)' : 'rgba(15,10,4,0.5)',
                  borderColor: revealedDog[i] ? 'rgba(212,165,74,0.3)' : 'rgba(120,80,20,0.1)',
                }}
                data-testid={`dog-answer-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold" style={{ color: theme.accentPrimary }}>{item.q}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${revealedDog[i] ? 'rotate-180' : ''}`} style={{ color: theme.navText }} />
                </div>
                {revealedDog[i] && (
                  <p className="mt-3 text-base leading-relaxed" style={{ color: theme.textPrimary }}>{item.a}</p>
                )}
              </button>
            ))}
          </div>

          <p className="mt-6 text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('illustration_conclusion')}
          </p>
        </div>

        {/* GIT TABLE — What it is / What it is not */}
        <div className="mb-12" data-testid="git-table">
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: theme.borderSecondary }}>
            <div className="grid grid-cols-2">
              <div className="p-4 font-bold text-sm text-center uppercase tracking-widest" style={{ background: theme.scriptureBg, color: theme.accentPrimary, borderBottom: '1px solid rgba(120,80,20,0.2)', borderRight: '1px solid rgba(120,80,20,0.2)' }}>
                What the GIT Is
              </div>
              <div className="p-4 font-bold text-sm text-center uppercase tracking-widest" style={{ background: theme.scriptureBg, color: theme.accentPrimary, borderBottom: '1px solid rgba(120,80,20,0.2)' }}>
                What the GIT Is Not
              </div>
            </div>
            {arr('git_table').map((row, i) => (
              <div key={i} className="grid grid-cols-2" style={{ borderBottom: i < arr('git_table').length - 1 ? '1px solid rgba(120,80,20,0.08)' : 'none' }}>
                <div className="p-4 text-sm leading-relaxed" style={{ color: 'rgba(220,190,130,0.8)', borderRight: '1px solid rgba(120,80,20,0.1)' }}>{row.is || ''}</div>
                <div className="p-4 text-sm leading-relaxed" style={{ color: 'rgba(220,190,130,0.6)' }}>{row.isnt || ''}</div>
              </div>
            ))}
          </div>
        </div>

        {/* THE ENCOURAGEMENT — Gold callout */}
        <div className="mb-12 rounded-xl p-6 border" style={{ background: 'rgba(212,165,74,0.06)', borderColor: 'rgba(212,165,74,0.25)' }} data-testid="encouragement-callout">
          <p className="text-base leading-relaxed" style={{ color: theme.accentPrimary }}>
            {c('encouragement_text')}
          </p>
        </div>

        {/* THE BEREAN STANDARD */}
        <div className="mb-12" data-testid="berean-standard">
          <h2 className="text-lg font-bold mb-4 tracking-wide" style={{ color: theme.labelColor }}>THE BEREAN STANDARD</h2>
          <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('berean_text')}
          </p>
        </div>

        {/* Beta Feedback */}
        <BetaFeedbackPanel moduleKey="GIT_101" />

        {/* BRIDGE CTA */}
        <div className="text-center py-8" data-testid="bridge-cta">
          <div className="h-px mx-auto mb-8" style={{ maxWidth: '200px', background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
          <p className="text-base mb-6 italic" style={{ color: 'rgba(220,190,130,0.7)' }}>
            {c('bridge_text')}
          </p>
          <p className="text-lg font-bold mb-8" style={{ color: theme.accentPrimary }}>
            {c('bridge_next')}
          </p>
          <button
            onClick={handleCompleteModule}
            disabled={completing}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all border"
            style={{
              background: 'linear-gradient(135deg, rgba(212,165,74,0.12), rgba(146,64,14,0.12))',
              borderColor: 'rgba(212,165,74,0.4)',
              color: theme.accentPrimary,
            }}
            data-testid="btn-complete-git101"
          >
            <BookOpen className="w-5 h-5" />
            {completing ? 'Marking Complete...' : 'I Have the Tool — Continue to The Blueprints'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
